import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { RefreshControl, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { LeaderboardRow } from '@/components/LeaderboardRow';
import { Overline } from '@/components/Overline';
import { PodiumCard, type PodiumVariant } from '@/components/PodiumCard';
import { Skeleton } from '@/components/Skeleton';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { leaderboardStore } from '@/stores/leaderboard.store';
import type { LeaderboardEntry } from '@/types/leaderboard';

const LeaderboardTab = observer(() => {
  useEffect(() => {
    void leaderboardStore.ensureLoaded();
  }, []);

  const onRefresh = useCallback(() => {
    leaderboardStore.refresh().catch(() => {
      // Error already captured on store.refreshError; .catch swallows the
      // rejection so RN doesn't fire an unhandled-promise yellow box.
    });
  }, []);

  const showInitialLoading =
    leaderboardStore.refreshing && !leaderboardStore.loaded;
  const showError =
    !!leaderboardStore.refreshError && !leaderboardStore.loaded;
  const showEmpty =
    leaderboardStore.loaded && leaderboardStore.entries.length === 0;
  const showPopulated =
    leaderboardStore.loaded && leaderboardStore.entries.length > 0;

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={
          <RefreshControl
            refreshing={leaderboardStore.refreshing && leaderboardStore.loaded}
            onRefresh={onRefresh}
            tintColor={colors.deepGreen}
          />
        }
      >
        <View style={styles.header}>
          <Overline>All-Time</Overline>
          <Heading size="5xl">Leaderboard</Heading>
        </View>

        {showInitialLoading && <LoadingState />}

        {showError && (
          <ErrorState
            title="Couldn't load leaderboard"
            body="Pull down to refresh, or tap retry."
            onRetry={() => {
              leaderboardStore.refresh().catch(() => {});
            }}
          />
        )}

        {showEmpty && (
          <EmptyState
            title="No rankings yet"
            body="Be first on the board — every referral counts."
          />
        )}

        {showPopulated && (
          <>
            <Podium entries={leaderboardStore.topThree} />
            {leaderboardStore.rest.length > 0 && (
              <Card padding={0} style={styles.listCard}>
                {leaderboardStore.rest.map((entry, i) => (
                  <LeaderboardRow
                    key={entry.rank}
                    entry={entry}
                    isMe={leaderboardStore.isMe(entry.rank)}
                    divider={i < leaderboardStore.rest.length - 1}
                  />
                ))}
              </Card>
            )}
            {leaderboardStore.myRank !== null && !leaderboardStore.inTopTwenty && (
              <PinnedRow rank={leaderboardStore.myRank} />
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
});

export default LeaderboardTab;

function Podium({ entries }: { entries: LeaderboardEntry[] }) {
  // Visual order: silver (left) | gold (center, taller) | bronze (right).
  // The handoff puts rank 1 in the middle so the eye lands there first.
  // Missing slots (early month with 1-2 entries) render as invisible spacers
  // so the 3-up grid stays intact — the gold card stays centered instead
  // of stretching full width.
  const slots: { idx: number; variant: PodiumVariant; flex: number }[] = [
    { idx: 1, variant: 'silver', flex: 1 },
    { idx: 0, variant: 'gold', flex: 1.1 },
    { idx: 2, variant: 'bronze', flex: 1 },
  ];
  return (
    <View style={styles.podium} testID="leaderboard-podium">
      {slots.map(({ idx, variant, flex }) => {
        const entry = entries[idx];
        if (!entry) {
          return <View key={variant} style={{ flex }} />;
        }
        return (
          <PodiumCard
            key={variant}
            entry={entry}
            variant={variant}
            isMe={leaderboardStore.isMe(entry.rank)}
            testID={`leaderboard-podium-${variant}`}
          />
        );
      })}
    </View>
  );
}

function LoadingState() {
  return (
    <View testID="leaderboard-skeleton" style={styles.skeletonStack}>
      <View style={styles.podium}>
        <Skeleton height={140} radius="lg" style={styles.skeletonPodiumSide} />
        <Skeleton height={180} radius="lg" style={styles.skeletonPodiumGold} />
        <Skeleton height={120} radius="lg" style={styles.skeletonPodiumSide} />
      </View>
      <Card padding={0} style={styles.listCard}>
        {[0, 1, 2, 3, 4].map((i) => (
          <View
            key={i}
            style={[styles.skeletonRow, i < 4 && styles.skeletonRowDivider]}
          >
            <Skeleton height={20} width={44} radius="sm" />
            <Skeleton height={36} width={36} radius="full" />
            <View style={styles.skeletonRowBody}>
              <Skeleton height={14} width="60%" />
            </View>
            <Skeleton height={20} width={56} radius="sm" />
          </View>
        ))}
      </Card>
    </View>
  );
}

function PinnedRow({ rank }: { rank: number }) {
  return (
    <View
      style={styles.pinnedRow}
      testID="leaderboard-pinned-row"
      accessible
      accessibilityRole="text"
      accessibilityLabel={`You are ranked number ${rank}`}
    >
      <Text style={styles.pinnedLabel}>You're #{rank}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  scroll: {
    padding: spacing.lg,
    gap: spacing.lg,
    paddingBottom: spacing['2xl'],
  },
  header: { gap: spacing.xs },
  podium: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.sm,
  },
  listCard: {
    overflow: 'hidden',
    padding: 0,
  },
  pinnedRow: {
    backgroundColor: colors.deepGreen,
    borderRadius: radii.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
  },
  pinnedLabel: {
    fontFamily: fonts.numeric,
    fontSize: fontSizes.lg,
    color: colors.frost,
    letterSpacing: 0.5,
  },
  skeletonStack: { gap: spacing.lg },
  skeletonPodiumSide: { flex: 1 },
  skeletonPodiumGold: { flex: 1.1 },
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.base,
  },
  skeletonRowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  skeletonRowBody: { flex: 1 },
});
